import React, { useEffect, useMemo, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { faTrash, faPenToSquare, faChevronDown, faChevronUp, faSlidersSimple } from "@fortawesome/pro-regular-svg-icons";
import ActionBar from "components/actionBar/ActionBar";

// Redux
import { showError, showSuccess } from "redux/actions/ui";

import { StatusCode } from "api/protocols";

import { AddTicketToVariant, DeleteTicketFromVariant } from "api/rpc/ticket";

import { ArchiveVariant, GetVariant, PutVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";

import { displayCurrency, roundNumber } from "helpers/Helpers";

import Page from "components/page/Page";
import Card from "components/card/Card";
import { ButtonNew as Button, ButtonNew } from "components/buttonNew";
import FormLayout from "components/form/FormLayout";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input/Input";
import { Select } from "components/select/";
import SelectNew from "components/select/SelectNew";
import Sheet from "components/sheet/Sheet";
import Tabs from "components/tabs/Tabs";
import { Badge } from "components/badge/Badge";
import DataTable from "../../../../components/dataTable/DataTable";

import "pages/secure/facility/product/Variant.scss";
import Popup from "components/popup/Popup";
import Toggle from "components/form/toggle/Toggle";
import { DuplicateVariant } from "api/rpc/2022-09/facilityAdmin/product/product";
import { isEqualWith, isNull } from "lodash";
import { GetTickets } from "api/rpc/2024-04/facilityAdmin/ticket/ticket";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import { TAccountingReference, TTaxLines } from "redux/reducers/models/facility";
import {
  accountingReferenceMatchesQuery,
  formatAccountingReferenceLabel,
} from "utils/formatAccountingReferenceLabel";
import {
  GetInventoryLevels,
  GetInventoryLogsAll,
  UpdateInventoryLevels,
} from "api/rpc/2024-04/facilityAdmin/product/inventory";
import { IProduct, IProductTicket, IVariant, TComboVariants, TProductUnit } from "redux/reducers/models/product";
import {
  AddProductsToVariant,
  UpdateComboProduct,
  RemoveProductFromVariant,
} from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetProductUnits } from "api/rpc/2024-04/facilityAdmin/product/unit";
import UpdateProductAsComboModal from "pages/secure/facility/product/modals/UpdateProductAsComboModal";
import AttachProductAsComboModal from "pages/secure/facility/product/modals/AttachProductAsComboModal";
import Portal from "elements/Portal";
import InventoryLogs from "components/inventoryLog/inventoryLog";
import { GetTaxLine } from "api/rpc/2024-04/facilityAdmin/client/taxLines";
import { nextSelectedTaxLineIds, taxLineSelectOptions, taxLineSelectValue } from "./taxLineSelect";
import { useUrlParams } from "hooks/useUrlParams/useUrlParams";
import InventoryHistoryTable from "./InventoryHistoryTable";
import RegisterInventoryTransferTable from "./RegisterInventoryTransferTable";

import useModal from "hooks/modals/useModal";
import { GetMembership, PutMembership, TFacilityMembership } from "api/rpc/2024-04/facilityAdmin/membership/membership";
import Spin from "components/spin/spin";
import { faArrowLeftArrowRight, faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import Search from "components/search/Search";
import FacilityInventoryTable from "pages/secure/facility/productNew/FacilityInventoryTable";
import { ERangeBucketSizeModal, isERangeProduct } from "pages/secure/facility/productNew/ERangeBucketSizeModal";

interface IVariantState extends IVariant {
  id: number;
  title: string;
  option1: string;
  option2: string;
  option3: string;
  price: number;
  original_price: number;
  cost: number;
  taxable: boolean;
  barcode: string;
  sku: string;
  editing: boolean;
  product: any;
  inventory_levels: any;
  quantity_available: number;
  showTickets: boolean;
  accounting_reference_id: number;
  cannot_be_sold: boolean;
  meta?: {
    variant_id: number;
    cannot_be_sold: number;
    facility_id: number;
    loyalty_points_override: string;
  };
  description: string;
  tickets: IProductTicket[];
  inventory_units?: number;
  warehouse_products?: Array<IProduct & { inventory_units?: string; variant_id?: number }>;
}

interface IVariantTicket {
  id: number;
  title: string;
  value: any;
  included: string;
  quantity: number;
}

// interface IOption {
//   id: number;
//   name: string;
//   value: string;
//   product_id: number;
// }

interface IAddTicketState {
  addTicketActive: boolean;
  currentAddedTickets: ITicket[];
  allTickets: any;
  ticketsToUpdate?: Map<any, any>;
  ticketSearch: string;
}

interface ITicket {
  id: number;
  title: string;
  subtitle: string;
  value: any;
  included: string;
  quantity: number;
}

interface IComboProductState {
  comboProductsActive?: boolean;
  currentAddedProducts?: TComboVariants[];
  productsToUpdate?: Map<any, any>;
  updateComboProductsOpen: boolean;
  selectedComboVariant: TComboVariants;
}

interface IParams {
  productId: string;
  variantId: string;
}

export default function Single(props: any) {
  const { Option } = Select;
  const history = useHistory();
  const { t } = useTranslation();
  const { productId, variantId } = useParams<IParams>();
  const dispatch = useAppDispatch();
  const productPermissions = props?.authStore?.user?.permissions;
  const facilityPermissions = props?.facilityStore?.facility?.permissions;
  const { searchParams } = useUrlParams({});

  const tabs = [
    {
      id: "overview",
      content: "Overview",
    },
    {
      id: "pricing",
      content: "Pricing & Taxes",
    },
    {
      id: "logistics",
      content: "Logistics",
    },
  ];

  const [reloadVariant, setReloadVariant] = useState<boolean>(false);
  const [eRangeBucketSize, setERangeBucketSize] = useState<number | null>(null);
  const [eRangeBucketModalOpen, setERangeBucketModalOpen] = useState(false);
  const [saveAfterBucketSelect, setSaveAfterBucketSelect] = useState(false);

  const foundTab = tabs.findIndex(t => t.id === searchParams.get('tab'));
  const initialSelectedState = foundTab < 0 ? 0 : foundTab;
  const [selected, setSelected] = useState(initialSelectedState);

  const [markup, setMarkup] = useState<number>(0);
  const [hidePriceAfterTax, setHidePriceAfterTax] = useState<boolean>(false);
  const [selectedTaxLine, setSelectedTaxLine] = useState<number[]>([]);
  const [selectedTaxLineBeforeChanges, setSelectedTaxLineBeforeChanges] = useState<number[]>([]);

  const [accountingReferences, setAccountingReferences] = useState<TAccountingReference[]>([]);
  const [duplicateVariantOpen, setDuplicateVariantOpen] = useState<boolean>(false);

  const [variantStateBeforeChanges, setVariantStateBeforeChanges] = useState<IVariantState>(undefined);
  const [ticketStateBeforeChanges, setTicketStateBeforeChanges] = useState<IAddTicketState>(undefined);
  const [variantLoaded, setVariantLoaded] = useState<boolean>(false);
  const [ticketsLoaded, setTicketsLoaded] = useState<boolean>(false);

  const [productUnits, setProductUnits] = useState<TProductUnit[]>(undefined);
  const [taxLines, setTaxLines] = useState<TTaxLines[]>(undefined);

  const [inventoryLogs, setInventoryLogs] = useState([]);

  const [priceAfterTax, setPriceAfterTax] = useState<number>(0);
  const defaultsSet = useRef(false);

  const initialVariantState: IVariantState = {
    id: null,
    title: "",
    option1: "",
    option2: "",
    option3: "",
    price: 0,
    original_price: 0,
    cost: 0,
    taxable: false,
    barcode: "",
    sku: "",
    editing: false,
    product: null,
    inventory_levels: null,
    quantity_available: null,
    showTickets: false,
    accounting_reference_id: null,
    cannot_be_sold: false,

    meta: {
      variant_id: null,
      cannot_be_sold: null,
      facility_id: null,
      loyalty_points_override: "",
    },
    description: "",
    inventory_item: null,
    inventory_units: null,
    tickets: [],
    combination_variants: [],
    facility_access: false,
    image_id: null,
    image: null,
    position: null,
    product_id: null,
  };
  const [variant, setVariant] = useState<IVariant>(null);
  const [variantState, setVariantState] = useState<IVariantState>(initialVariantState);

  const initialAddTicketState: IAddTicketState = {
    addTicketActive: false,
    currentAddedTickets: [],
    allTickets: [],
    ticketsToUpdate: new Map(),
    ticketSearch: "",
  };
  const [addTicketState, setAddTicketState] = useState<IAddTicketState>(initialAddTicketState);

  const [filterState, setFilterState] = useState({
    search: "",
    showAccountingReference: false,
    showDescription: false,
  });

  const [comboProductState, setComboProductState] = useState<IComboProductState>({
    comboProductsActive: false,
    currentAddedProducts: [],
    productsToUpdate: new Map(),
    updateComboProductsOpen: false,
    selectedComboVariant: null,
  });

  const {
    state: attachMembershipModal,
    updateModal: updateAttachMembershipModal,
    closeModal: closeAttachMembershipModal,
  } = useModal({
    isOpen: false,
  });

  const [membershipState, setMembershipState] = useState<{
    memberships: TFacilityMembership[];
    loading: boolean;
  }>({
    memberships: [],
    loading: false,
  });


  const [attachedMemberships, setAttachedMemberships] = useState<TFacilityMembership[]>([]);
  const [membershipSearchQuery, setMembershipSearchQuery] = useState("");
  const [selectedMembershipId, setSelectedMembershipId] = useState<number | null>(null);
  const [availableMemberships, setAvailableMemberships] = useState<TFacilityMembership[]>(undefined);

  const [selectedComboProductIds, setSelectedComboProductIds] = useState<number[]>([]);

  const allComboProductsSelected = useMemo(() => {
    if (comboProductState.currentAddedProducts == null || comboProductState.currentAddedProducts.length === 0) {
      return false;
    } else {
      return comboProductState.currentAddedProducts.every(comboProduct =>
        selectedComboProductIds.includes(comboProduct.id),
      );
    }
  }, [comboProductState.currentAddedProducts, selectedComboProductIds]);

  const enableComboProductActions = productPermissions?.products_edit;

  const defaultAccountingReference = accountingReferences.find(
    (ref: Record<string, any>) => ref?.id == variantState.accounting_reference_id,
  );

  useEffect(() => {
    ReactDOM.unstable_batchedUpdates(() => {
      setSelected(initialSelectedState);
      setVariantState(initialVariantState);
      setAddTicketState(initialAddTicketState);
      setReloadVariant(true);
    });
  }, [variantId]);


  useEffect(() => {
    if (reloadVariant) {
      void loadVariant();
      void loadInventoryLevels();
      void loadAccountingReference();
      void loadProductUnits();
      void loadTaxLines();
      setReloadVariant(false);
    }
  }, [reloadVariant]);

  useEffect(() => {
    if (!defaultsSet.current && variant && taxLines) {
      setMarkup(((variant?.price - variant?.cost) / variant?.cost) * 100);
      setPriceAfterTax(Number(getPriceAfterTax(variant?.price ?? 0)));
      defaultsSet.current = true;
    }
  }, [variant, taxLines]);


  useEffect(() => {
    if (attachMembershipModal.isOpen) {
      void loadAvailableMemberships();
    }
  }, [attachMembershipModal.isOpen, membershipSearchQuery]);

  useEffect(() => {
    if (variantState.product?.id) {
      void loadProductMemberships();
    }
  }, [variantState.product?.id]);

  //preset the accounting reference search value
  useEffect(() => {
    const id = variantState.accounting_reference_id;
    if (id === null || id === undefined) {
      setFilterState(prev => ({ ...prev, search: "" }));
      return;
    }
    if (!accountingReferences?.length) {
      return;
    }
    const ref = accountingReferences.find(
      (r: TAccountingReference) => String(r?.id) === String(id),
    );
    if (!ref) {
      return;
    }
    setFilterState(prev => ({
      ...prev,
      search: formatAccountingReferenceLabel(ref.title, ref.reference_number),
    }));
  }, [variantState.accounting_reference_id, accountingReferences]);

  async function loadInventoryLevels() {
    const inventoryRes = await GetInventoryLevels(
      {
        variant_id: variantId,
        extended: true,
      },
      true,
    );

    if (inventoryRes.status !== StatusCode.OK) {
      return;
    }

    const inventory = inventoryRes.data;

    setVariantState(prev => ({
      ...prev,
      inventory_levels: inventory,
    }));

    setVariantStateBeforeChanges(prev => ({
      ...prev,
      inventory_levels: inventory,
    }));

    const tax_ids: number[] = inventory[0].tax_lines?.map((line: TTaxLines) => line.id);

    setSelectedTaxLine(tax_ids ?? []);
    setSelectedTaxLineBeforeChanges(tax_ids ?? []);
  }

  async function loadVariant() {
    const variantRes = await GetVariant({ id: Number(variantId), extended: true, combination_variants: true, warehouse_products: true }, true);
    if (variantRes.status !== StatusCode.OK) {
      return;
    }

    const variant = variantRes.data[0];

    let showTicket = false;

    if (variant.product.type === "Ticket") {
      showTicket = true;
    }

    const tempTicketList: ITicket[] = [];
    const tempTicketsToUpdate = new Map();

    for (let i = 0; i < variant.tickets.length; i++) {
      const tempTicket = {
        id: variant.tickets[i].id,
        title: variant.tickets[i].title,
        subtitle: variant.tickets[i].subtitle,
        value: variant.tickets[i].value,
        included: variant.tickets[i].included,
        quantity: variant.tickets[i].quantity,
      };

      tempTicketList.push(tempTicket);
      tempTicketsToUpdate.set(variant.tickets[i].id, variant.tickets[i].quantity);
    }

    if (variant.description && variant.description.length > 0) {
      setFilterState(prevState => ({ ...prevState, showDescription: true }));
    }


    setVariant(variant);
    setVariantState(prev => ({
      ...prev,
      id: variant.id,
      title: variant.title,
      option1: variant.option1,
      option2: variant.option2,
      option3: variant.option3,
      price: variant.price,
      original_price: variant.original_price === null ? 0 : variant.original_price,
      cost: variant.cost,
      taxable: variant.taxable,
      barcode: variant.barcode === null ? "" : variant.barcode,
      sku: variant.sku === null ? "" : variant.sku,
      product: variant.product,
      quantity_available:
        variant.inventory_item.inventory_level.quantity_available === null
          ? 0
          : variant.inventory_item.inventory_level.quantity_available,
      showTickets: showTicket,
      accounting_reference_id: variant.accounting_reference_id,
      cannot_be_sold: variant.cannot_be_sold,
      meta: {
        cannot_be_sold: variant.meta?.cannot_be_sold ?? 0,
        variant_id: variant.id,
        facility_id: variant.meta?.facility_id,
        loyalty_points_override: variant?.meta?.loyalty_points_override ? variant?.meta?.loyalty_points_override?.toString() : "",
      },
      description: variant.description,
      tickets: variant.tickets,
      inventory_item: variant.inventory_item,
      inventory_units: variant.inventory_units,
      warehouse_products: variant?.warehouse_products ?? [],
      image_id: variant.image_id ?? null,
      image: variant.image ?? null,
      integration_data: variant.integration_data ?? null,
    }));

    const bucketSize = variant.integration_data?.e_range_bucket_size ?? null;
    setERangeBucketSize(bucketSize);
    if (isERangeProduct(variant.product?.type) && bucketSize == null) {
      setSaveAfterBucketSelect(false);
      setERangeBucketModalOpen(true);
    }

    setAddTicketState(prev => ({
      ...prev,
      currentAddedTickets: tempTicketList,
      ticketsToUpdate: tempTicketsToUpdate,
    }));

    setComboProductState(prev => ({
      ...prev,
      currentAddedProducts: variant.combination_variants,
    }));

    setVariantStateBeforeChanges(undefined);
    setTicketStateBeforeChanges(undefined);

    setTicketsLoaded(true);
    setVariantLoaded(true);
  }

  //loads dropdown AFTER the variantState has been loaded
  async function loadAccountingReference() {
    const accountingRes = await GetAccountingReference(null, false);
    if (accountingRes.status !== StatusCode.OK) {
      return;
    }

    //I do not believe we are using accounts variable after this ?
    const accounts = accountingRes.data;
    accounts.unshift({
      id: null,
      title: "",
    });

    setAccountingReferences(accountingRes.data);
  }

  async function loadProductUnits() {
    const unitRes = await GetProductUnits(null, false);

    if (unitRes.status !== StatusCode.OK) {
      return;
    }

    setProductUnits(unitRes.data);
  }

  async function loadTaxLines() {
    const unitRes = await GetTaxLine(null, false);

    if (unitRes.status !== StatusCode.OK) {
      return;
    }

    setTaxLines(unitRes.data);
  }

  function variantNavigationOptions() {
    const productId = variantState.product?.id;

    if (productId === undefined || productId === null || variantState.product.variants === undefined) {
      return [];
    }

    const currentVariantIndex: number = variantState.product.variants.findIndex(
      (variant: any) => variant.id === variantState.id,
    );

    if (currentVariantIndex === -1) {
      return [];
    }

    const variantNavigationOptionList = [];

    if (currentVariantIndex - 1 >= 0) {
      variantNavigationOptionList.push({
        content: <FontAwesomeIcon className="variant-navigation-arrow" icon={["far", "arrow-left"]} />,
        action: () =>
          navigateToVariant(productId as number, variantState.product.variants[currentVariantIndex - 1].id as number),
      });
    }

    if (currentVariantIndex + 1 < variantState.product.variants.length) {
      variantNavigationOptionList.push({
        content: <FontAwesomeIcon className="variant-navigation-arrow" icon={["far", "arrow-right"]} />,
        action: () =>
          navigateToVariant(productId as number, variantState.product.variants[currentVariantIndex + 1].id as number),
      });
    }

    return variantNavigationOptionList;
  }

  function navigateToVariant(productId: number, variantId: number) {
    const url = "/admin/product/" + String(productId) + "/variant/" + String(variantId);
    history.push(url);
  }

  async function saveVariant(bucketSizeOverride?: number) {
    const bucketSize = typeof bucketSizeOverride === "number" ? bucketSizeOverride : eRangeBucketSize;
    const eRangeProduct = isERangeProduct(variantState.product?.type);
    if (eRangeProduct && bucketSize == null) {
      setSaveAfterBucketSelect(true);
      setERangeBucketModalOpen(true);
      return;
    }

    for (let i = 0; i < variantState.tickets?.length; i++) {
      const ticket = variantState.tickets[i];

      const addTicketToVariantRes = await AddTicketToVariant(
        {
          ticket_id: Number(ticket.id),
          variant_id: Number(variantState.id),
          quantity: Number(ticket.quantity),
        },
        true,
      );

      if (addTicketToVariantRes.status !== StatusCode.OK) {
        // uiActions.showError(addTicketToVariantRes.data.message);
        return;
      }
    }

    const warehouseProductsParam = variantState?.warehouse_products ? variantState?.warehouse_products?.map(product => ({ warehouse_product_id: product?.id, inventory_units: product?.inventory_units ?? null })) : [];

    const variantRes = await PutVariant(
      {
        ...variantState,
        taxable: selectedTaxLine.length > 0,
        warehouse_product_ids: warehouseProductsParam,
        ...(eRangeProduct && bucketSize != null
          ? { integration_data: { e_range_bucket_size: bucketSize } }
          : {}),
      },
      true,
    );
    if (variantRes.status !== StatusCode.OK) {
      props.uiActions.showError(t("secure.facility.product.variant.036"));
      return;
    }

    const res = await UpdateInventoryLevels({
      variant_id: variantState.id,
      tax_line_ids: selectedTaxLine,
    }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.use_edit_product.031")));
      return;
    }

    setVariantState(variantRes.data as IVariantState);
    setVariantStateBeforeChanges(variantRes.data as IVariantState);
    setSelectedTaxLineBeforeChanges(selectedTaxLine);
    props.uiActions.showSuccess(t("secure.facility.product.variant.037"));
  }

  async function DeleteVariant() {
    const archiveRes = await ArchiveVariant({ id: variantId }, true);

    if (archiveRes.status !== StatusCode.OK) {
      props.uiActions.showError(t("secure.facility.product.variant.038"));
      return;
    }

    props.uiActions.showSuccess(t("secure.facility.product.variant.039"));
    history.push("/admin/product/" + String(productId));
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setVariantState(prevState => ({ ...prevState, [id]: value }));
  }

  function updateFilterStateBoolean(property: string) {
    setFilterState(prevState => ({ ...prevState, [property]: true }));
  }

  function openAddTicket() {
    setAddTicketState(prev => ({
      ...prev,
      addTicketActive: true,
    }));
  }

  function closeAddTicket() {
    setAddTicketState(prev => ({
      ...prev,
      addTicketActive: false,
      ticketSearch: "",
    }));
  }

  async function handleAddTicket(_: any, ticket: any) {
    //Add ticket ID to product?
    const params = {
      ticket_id: Number(ticket.id),
      variant_id: Number(variantState.id),
      quantity: 1,
    };

    const addTicketToVariantRes = await AddTicketToVariant(params, true);
    if (addTicketToVariantRes.status !== StatusCode.OK) {
      return;
    }

    closeAddTicket();

    //Reload Product
    void loadVariant();
  }

  async function handleDeleteTicket(_: any, ticket: any) {
    const params = {
      ticket_id: Number(ticket.id),
      variant_id: Number(variantState.id),
    };

    const deleteTicketRes = await DeleteTicketFromVariant(params, true);
    if (deleteTicketRes.status !== StatusCode.OK) {
      return;
    }

    void loadVariant();
  }

  function handleTicketQuantityChange(event: any, ticket: any) {
    const newQuantity = Number(event.target.value);
    const newState = variantState.tickets?.map((obj: IVariantTicket) => {
      if (obj.id === ticket.id) {
        return { ...obj, quantity: newQuantity };
      }

      return obj;
    });

    setVariantState(prevState => ({
      ...prevState,
      tickets: newState,
    }));
  }

  // function handleTicketQuantityChange(event: any, ticket: any) {
  //   const newQuantity = event.target.value;
  //   const tempTicketsToUpdate = addTicketState.ticketsToUpdate;

  //   tempTicketsToUpdate.set(ticket.id, newQuantity);

  //   setAddTicketState((prev) => ({
  //     ...prev,
  //     ticketsToUpdate: tempTicketsToUpdate,
  //   }));
  // }

  async function displayInventoryLogs() {
    setShowInventoryLogs(true);

    const response = await GetInventoryLogsAll(
      { inventory_level_id: variantState?.inventory_item?.inventory_level?.id },
      false,
    );

    if (response.status !== StatusCode.OK) {
      dispatch(showError("Error displaying inventory logs"));
      return;
    }

    setInventoryLogs(response.data as any[]);
  }

  // Handle tab change
  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  async function loadAvailableMemberships() {
    const res = await GetMembership(
      {
        search: membershipSearchQuery,
        limit: 500,
        offset: 0,
        archived: false,
        filters: [],
      },
      false,
    );

    if (res.status !== StatusCode.OK) {
      console.error("Error loading memberships");
      return;
    }

    const allMemberships = Array.isArray(res.data) ? res.data : [];
    const filtered = allMemberships.filter(
      membership => !attachedMemberships.some(attached => attached.id === membership.id),
    );

    setAvailableMemberships(filtered);
  }

  async function loadProductMemberships() {
    if (!variantState.product?.id) {
      return;
    }

    setMembershipState(prev => ({ ...prev, loading: true }));

    const res = await GetMembership({ limit: 500, offset: 0, archived: false }, false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading memberships")));
      setMembershipState(prev => ({ ...prev, loading: false }));
      return;
    }

    const allMemberships = Array.isArray(res.data) ? res.data : [];
    const membershipsUsingVariant = allMemberships.filter(m => m.variant_id === Number(variantId));

    setAttachedMemberships(membershipsUsingVariant);
    setMembershipState({
      memberships: membershipsUsingVariant,
      loading: false,
    });
  }


  async function handleAttachMembership() {
    if (!selectedMembershipId) {
      return;
    }

    const membershipToAttach = availableMemberships?.find(m => m.id === selectedMembershipId);
    const currentMembership = attachedMemberships[0];

    if (currentMembership) {
      const res = await PutMembership({ id: currentMembership.id, product_id: membershipToAttach?.product_id, variant_id: membershipToAttach?.variant_id }, true);

      if (res.status !== StatusCode.OK) {
        dispatch(showError(t("Membership cannot be detached")));
        return;
      }
    }

    const res = await PutMembership(
      { id: selectedMembershipId, product_id: Number(productId), variant_id: Number(variantId) },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Membership cannot be attached")));
      return;
    }

    if (membershipToAttach) {
      setAttachedMemberships([membershipToAttach]);
      dispatch(showSuccess(t("Membership attached successfully")));
    }

    setSelectedMembershipId(null);
    setMembershipSearchQuery("");
    closeAttachMembershipModal();
    void loadProductMemberships();
  }


  async function completeUpdateInventory(newQuantity: { price: number, cost: number, quantity: number }) {
    const params = {
      variant_id: variantState.id,
      quantity_available: newQuantity.quantity + variant.inventory_item.inventory_level.quantity_available,
      cost: newQuantity.cost,
      price: newQuantity.price,
    };

    const updateInventoryRes = await UpdateInventoryLevels(params, true);
    if (updateInventoryRes.status !== StatusCode.OK) {
      return;
    }

    setAdjustQuantity(false);

    //Force refresh
    void loadVariant();
    void loadInventoryLevels();
  }

  // Search Tickets
  useEffect(() => {
    let mounted = true;
    let timeoutId: number | null = null;
    const search = () => {
      timeoutId = window.setTimeout(() => {
        void (async () => {
          if (addTicketState.ticketSearch !== null) {
            try {
              const res = await loadTickets(addTicketState.ticketSearch);
              console.log("----> ticketRes", res);
              if (mounted) {
                setAddTicketState(prevState => ({ ...prevState, allTickets: res }));
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          }
        })();
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [addTicketState.ticketSearch]);

  async function loadTickets(search: any) {
    const ticketRes = await GetTickets({ search: search }, true);

    if (ticketRes.status === StatusCode.OK) {
      setAddTicketState(prevState => ({
        ...prevState,
        allTickets: ticketRes.data,
      }));

      return ticketRes.data;
    }

    return [];
  }

  function handleSelectOnSearch(query: string) {
    if (query.length === 0) {
      setVariantState({ ...variantState, accounting_reference_id: null });
    }

    setFilterState({ ...filterState, search: query });
  }

  async function duplicateVariant() {
    const res = await DuplicateVariant({ variant_id: variantId }, true);

    if (res.status !== StatusCode.OK) {
      props.uiActions.showError(t("secure.facility.product.variant.040"));

      return;
    }

    setDuplicateVariantOpen(false);
    history.push("/admin/product/" + String(productId));
  }

  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;

    setVariantState(prevState => ({ ...prevState, meta: { ...prevState.meta, [id]: Number(checked) } }));
  }

  /* Always refreshes product on success */
  async function addComboProduct(params: { base_variant_id: number; variant_id: number }) {
    const res = await AddProductsToVariant(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.variant.041")));
      return;
    }

    void loadVariant();
    setComboProductState(prevState => ({ ...prevState, comboProductsActive: false }));
  }

  async function removeProductsFromCombo(comboVariantIds: number[]) {
    const params = {
      base_variant_id: Number(variantId),
      variant_id: comboVariantIds,
    };

    const res = await RemoveProductFromVariant(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.variant.042")));
      return;
    }

    void loadVariant();
  }

  /** TODO: Connect to backend || updateComboProduct function within useEditProduct hook */
  function handleUpdateComboProductModalOk(updateId: number, cost: string, price: string, quantity: string) {
    const params = {
      base_variant_id: Number(variantId),
      variant_id: updateId,
      cost: cost,
      price: price,
      quantity: quantity,
    };

    void updateComboProduct(params);
    setComboProductState(prevState => ({ ...prevState, updateComboProductsOpen: false, selectedComboVariant: null }));
  }

  /* Always refreshes product on success */
  async function updateComboProduct(params: {
    base_variant_id: number;
    variant_id: number;
    cost: string;
    price: string;
    quantity: string;
  }) {
    const res = await UpdateComboProduct(
      {
        base_variant_id: params.base_variant_id,
        variants_to_attach: [
          {
            variant_id: Number(params.variant_id),
            cost: Number(params.cost),
            price: Number(params.price),
            quantity: Number(params.quantity),
          },
        ],
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.variant.043")));
      return;
    }

    void loadVariant();
  }

  function unsavedChangesExist() {
    if (variantStateBeforeChanges === undefined) {
      if (variantLoaded) {
        setVariantStateBeforeChanges(variantState);
      }
      return false;
    }

    if (ticketStateBeforeChanges === undefined) {
      if (ticketsLoaded) {
        setTicketStateBeforeChanges(addTicketState);
      }
      return false;
    }

    return (
      !isEqualWith(variantStateBeforeChanges, variantState, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      }) ||
      !isEqualWith(
        ticketStateBeforeChanges.ticketsToUpdate,
        addTicketState.ticketsToUpdate,
        (originalValue, newValue) => {
          if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
            return true;
          }
        },
      ) ||
      !isEqualWith(selectedTaxLine, selectedTaxLineBeforeChanges, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      })
    );
  }

  function cancelUnsavedChanges() {
    setVariantState(variantStateBeforeChanges);
    setAddTicketState(ticketStateBeforeChanges);
    setSelectedTaxLine(selectedTaxLineBeforeChanges);
  }

  const toggleTaxLines = (value: number | string) => {
    const next = nextSelectedTaxLineIds(selectedTaxLine, value);
    setSelectedTaxLine(next);
    setVariantState(prev => ({ ...prev, taxable: next.length > 0 }));
  };

  useEffect(() => {
    if (defaultsSet.current && taxLines) {
      setPriceAfterTax(getPriceAfterTax(variantState.price));
    }
  }, [selectedTaxLine, taxLines]);

  function getTaxNames() {
    if (!taxLines) {
      return;
    }
    const taxNames = [];
    for (let i = 0; i < selectedTaxLine.length; i++) {
      taxNames[i] = taxLines.find(line => line.id === selectedTaxLine[i]).name;
    }
    return taxNames.join(", ");
  }

  // function handleRemoveComboProduct(combo: TComboVariants) {
  //   if (enableComboProductActions) {
  //     void removeProductsFromCombo([combo.id]);
  //     setSelectedComboProductIds(prev => prev.filter(selectedId => selectedId !== combo.id));
  //   }
  // }

  // function handleUpdateComboProduct(combo: TComboVariants) {
  //   if (enableComboProductActions) {
  //     setComboProductState(prev => ({
  //       ...prev,
  //       updateComboProductsOpen: true,
  //       selectedComboVariant: combo,
  //     }));
  //   }
  // }

  function handleSelectAllComboProducts() {
    if (comboProductState.currentAddedProducts == null || comboProductState.currentAddedProducts.length === 0) {
      return;
    } else if (allComboProductsSelected) {
      setSelectedComboProductIds([]);
    } else {
      setSelectedComboProductIds(comboProductState.currentAddedProducts.map(comboProduct => comboProduct.id));
    }
  }

  function handleSelectComboProduct(comboId: number) {
    if (selectedComboProductIds.includes(comboId)) {
      setSelectedComboProductIds(prev => prev.filter(selectedComboId => selectedComboId !== comboId));
    } else {
      setSelectedComboProductIds(prev => [...prev, comboId]);
    }
  }

  function handleComboProductsRemoval() {
    if (enableComboProductActions) {
      void removeProductsFromCombo([...selectedComboProductIds]);
      setSelectedComboProductIds([]);
    }
  }

  function handleMarkupChange(value: number) {
    if (!variantState?.cost) {
      showError("Cost is required");
    } else {
      setMarkup(value);
      setVariantState(prev => ({
        ...prev,
        price: Number(roundNumber(variantState.cost * (value / 100) + variantState.cost, 2)),
      }));
      setPriceAfterTax(Number(getPriceAfterTax(Number(roundNumber(variantState.cost * (value / 100) + variantState.cost, 2)))));
    }
  }

  function handlePriceAfterTaxChange(value: number) {
    const cost = Number(variantState?.cost ?? 0);

    let totalTaxRate = 0;
    for (let i = 0; i < selectedTaxLine.length; i++) {
      const taxLine = taxLines.find(tax => tax.id === selectedTaxLine[i]);
      if (taxLine) {
        totalTaxRate += Number(taxLine.tax);
      }
    }

    const price = value / (1 + totalTaxRate);

    setMarkup(((price - cost) / cost) * 100);
    setVariantState(prev => ({
      ...prev,
      price: Number(roundNumber(price, 2)),
    }));
    setPriceAfterTax(value);
  }

  function getPriceAfterTax(value: number) {
    const originalPrice = value;
    let price = value;
    for (let i = 0; i < selectedTaxLine.length; i++) {
      price += originalPrice * taxLines.find(tax => tax.id === selectedTaxLine[i])?.tax;
    }
    return Number(roundNumber(price, 2));
  }

  function handleCostChange(value: number) {
    if (value === 0) {
      setMarkup(0);
      setVariantState(prev => ({
        ...prev,
        cost: Number(roundNumber(value, 2)),
      }));
      return;
    }
    const price = Number(variantState?.price ?? 0);
    setMarkup(((price - value) / value) * 100);
    setVariantState(prev => ({
      ...prev,
      cost: Number(roundNumber(value, 2)),
    }));
  }

  function handlePriceChange(value: number) {
    const cost = Number(variantState?.cost ?? 0);
    setMarkup(((value - cost) / cost) * 100);
    setVariantState(prev => ({
      ...prev,
      price: Number(roundNumber(value, 2)),
    }));
    setPriceAfterTax(Number(getPriceAfterTax(value)));
  }

  function onInventoryUnitsChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
    const { value } = e.target;
    const updatedWarehouseProducts = [...variantState.warehouse_products];
    if (updatedWarehouseProducts[index]) {
      updatedWarehouseProducts[index] = { ...updatedWarehouseProducts[index], inventory_units: value };
      setVariantState(prev => ({ ...prev, warehouse_products: updatedWarehouseProducts }));
    }
  }

  const sectionDivider = <div className="border-gray-200 border-b mx-4 lg:mt-3"></div>;
  const productUnit = productUnits?.find(unit => unit.id === variantState.product?.product_unit_id);
  const [adjustQuantity, setAdjustQuantity] = useState<boolean>(false);
  const [showInventoryLogs, setShowInventoryLogs] = useState<boolean>(false);
  const [showAllFacilityInventories, setShowAllFacilityInventories] = useState<boolean>(false);
  const { facilityStore } = useAppSelector(store => store);

  return (
    <>
      <Page
        title={variantState.title}
        subtitle={`${String(variantState.product?.title)}${variantState.product?.subtitle ? " - " + String(variantState.product?.subtitle) : ""
          }`}
        narrow
        // primaryAction={primaryAction}
        secondaryActions={variantNavigationOptions()}
        multipleActionDropdownAction={{
          label: t("secure.facility.product.variant.044"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("secure.facility.product.variant.045"),
                handler: () => DeleteVariant(),
                disabled: !productPermissions?.products_edit || !facilityPermissions?.products_edit,
                icon: "trash",
              },
            ],
          },
        }}
        breadcrumbs={[
          {
            prefix: true,
            label: t("Back to Product"),
            url: "/admin/product/" + String(variantState.product?.id),
          },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: () => void saveVariant(),
          onCancel: cancelUnsavedChanges,
        }}
      >
        <Page.Section>
          <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} fitted />
          <br />
          {selected === 0 ? (
            <>
            <Card title={t("secure.facility.product.variant.005")}>
              <Card.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      value={variantState.title}
                      label={t('Title')}
                      id="title"
                      placeholder={t('Title')}
                      onChange={handleInputChange}
                      readOnly={!productPermissions?.products_edit || !facilityPermissions?.products_edit}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
              {facilityStore?.loyaltyProgram ? (
                <Card.Section title={t("Loyalty Points Override")}>
                  <Input
                    value={variantState?.meta?.loyalty_points_override ?? ""}
                    label={t('Points Earned When Purchased')}
                    id="loyalty_points_override"
                    //placeholder={t("Placeholder")}
                    tooltipText={t("This field allows you to set a specific number of loyalty points earned for purchasing this product. If no value is set, the default loyalty points set in the Loyalty Program section will be used.")}
                    type="number"
                    onChange={e => setVariantState(prevState => ({ ...prevState, meta: { ...prevState.meta, loyalty_points_override: e.target.value } }))}
                    readOnly={!productPermissions?.products_edit || !facilityPermissions?.products_edit}
                  />
                </Card.Section>
              ) : null}
            </Card>
            </>
          ) : null}
          {selected === 1 ? (
            <>
              <Card title={t("Pricing")}>
                <Card.Section>
                  <FormLayout>
                    <FormLayout.Group>
                      <Input
                        id="retail_price"
                        value={variantState.price || ""}
                        type="number"
                        prefix="$"
                        label={t("Retail Price")}
                        onChange={e => handlePriceChange(Number(e.target.value))}
                        placeholder="0.00"
                        disabled={
                          !productPermissions?.products_edit ||
                          !productPermissions?.products_edit_price ||
                          variantState.product?.price_locked ||
                          (variantState.product?.type === 'Combo' && variantState.product?.dynamic_combo !== true)
                        }
                      //error={!validated("price") && attempted}
                      />
                      <Input
                        id="cost"
                        value={variantState.cost || ""}
                        type="number"
                        prefix="$"
                        label={t("secure.facility.product.product_new.015")}
                        onChange={e => handleCostChange(Number(e.target.value))}
                        placeholder="0.00"
                        disabled={
                          !productPermissions?.products_edit ||
                          !productPermissions?.products_edit_cost ||
                          (variantState.product?.type === 'Combo' && variantState.product?.dynamic_combo !== true)

                        }
                      //error={!validated("cost") && attempted}
                      />
                      <Input
                        id="markup"
                        value={Number(roundNumber(markup, 2)) || ""}
                        onChange={e => handleMarkupChange(Number(e.target.value))}
                        type="number"
                        prefix="%"
                        label={t("Markup")}
                        placeholder="0.00"
                        disabled={
                          !productPermissions?.products_edit ||
                          !productPermissions?.products_edit_price ||
                          variantState.product?.price_locked ||
                          (variantState.product?.type === 'Combo' && variantState.product?.dynamic_combo !== true)
                        }
                      />
                    </FormLayout.Group>
                  </FormLayout>
                </Card.Section>
                <Card.Section
                  title={t("Price after tax")}
                  sectionTitleActions={[
                    {
                      content: <FontAwesomeIcon icon={hidePriceAfterTax ? faChevronUp : faChevronDown} />,
                      action: () => {
                        setHidePriceAfterTax(!hidePriceAfterTax);
                      },
                    },
                  ]}
                >
                  {!hidePriceAfterTax ? (
                    <FormLayout>
                      <FormLayout.Group>
                        <SelectNew
                          id="tax-lines-select"
                          label={t("Tax lines")}
                          placeholder={selectedTaxLine.length === 0 ? t("No tax") : getTaxNames() || t("Select tax lines")}
                          multiValue={taxLineSelectValue(selectedTaxLine)}
                          options={taxLineSelectOptions(taxLines, t("No tax"))}
                          onChange={value => toggleTaxLines(value)}
                          disabled={
                            !productPermissions?.products_edit ||
                            (variantState.product?.type === 'Combo' && variantState.product?.dynamic_combo !== true)
                          }
                        />
                        <Input
                          id="price_after_tax"
                          value={priceAfterTax || ""}
                          type={"number"}
                          prefix="$"
                          label={t("Price after tax")}
                          placeholder="0.00"
                          //error={!validated("price") && attempted}
                          onChange={e => handlePriceAfterTaxChange(Number(e.target.value))}
                          disabled={
                            !productPermissions?.products_edit ||
                            !productPermissions?.products_edit_price ||
                            variantState.product?.price_locked ||
                            (variantState.product?.type === 'Combo' && variantState.product?.dynamic_combo !== true)
                          }
                        />
                      </FormLayout.Group>
                    </FormLayout>
                  ) : null}
                </Card.Section>

              </Card>
              {variantState?.product?.type === "Combo" ? (
                <Card
                  title={t("secure.facility.product.variant.046")}
                  titleActions={[
                    {
                      content: t("secure.facility.product.variant.047"),
                      action: () => setComboProductState(prev => ({ ...prev, comboProductsActive: true })),
                      disabled: !productPermissions?.products_edit,
                    },
                  ]}
                >
                  <Card.Section>
                    <div className="product-edit-combo-table">
                      <ActionBar
                        visible={selectedComboProductIds?.length > 0}
                        selectedItemIds={selectedComboProductIds}
                        clearSelectedItemIds={() => setSelectedComboProductIds([])}
                        itemSingularTitle={t("client_side.product.variant.013")}
                        itemPluralTitle={t("client_side.product.variant.014")}
                        primaryActions={[
                          {
                            icon: faTrash,
                            title: t("client_side.product.variant.015"),
                            action: handleComboProductsRemoval,
                          },
                        ]}
                      >
                        <DataTable
                          columns={[
                            {
                              label: t("secure.facility.product.variant.048"),
                              content: (
                                <Checkbox
                                  label={t("secure.facility.product.variant.048")}
                                  size="small"
                                  checked={allComboProductsSelected}
                                  onChange={handleSelectAllComboProducts}
                                />
                              ),
                              width: "35%",
                            },
                            { label: t("Quantity"), width: "10%" },
                            { label: t("secure.facility.product.variant.049"), width: "20%" },
                            { label: t("secure.facility.product.variant.050"), width: "20%" },
                            { label: t("secure.facility.product.variant.051"), width: "15%" },
                            { label: "", width: "5%" }, // Remove column
                            { label: "", width: "5%" }, // Edit column
                          ]}
                        >
                          {comboProductState.currentAddedProducts?.map(combo => (
                            <tr key={combo.id}>
                              <td>
                                <span className="flex flex-row">
                                  <Checkbox
                                    size="small"
                                    labelHidden
                                    checked={selectedComboProductIds.includes(combo.id)}
                                    onChange={() => handleSelectComboProduct(combo.id)}
                                  />
                                  <div>
                                    <p className="variant-combo-variant-title">{combo.product.title}</p>
                                    {combo.product.subtitle && (
                                      <p className="variant-combo-variant-subtitle">{combo.product.subtitle}</p>
                                    )}
                                  </div>
                                </span>
                              </td>
                              <td>{combo.combo_pivot.quantity}</td>
                              <td>
                                {combo?.combo_pivot?.price
                                  ? displayCurrency("cad", parseFloat(combo?.combo_pivot.price))
                                  : ""}
                              </td>
                              <td>
                                {combo?.combo_pivot?.cost
                                  ? displayCurrency("cad", parseFloat(combo?.combo_pivot.cost))
                                  : ""}
                              </td>
                              <td>
                                <Badge>{combo.product.type}</Badge>
                              </td>
                              <td>
                                <Button
                                  type="text"
                                  onClick={() => removeProductsFromCombo([combo.id])}
                                  disabled={!productPermissions?.products_edit}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </td>
                              <td>
                                <Button
                                  type="text"
                                  onClick={() =>
                                    setComboProductState(prev => ({
                                      ...prev,
                                      updateComboProductsOpen: true,
                                      selectedComboVariant: combo,
                                    }))
                                  }
                                  disabled={!productPermissions?.products_edit}
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </DataTable>
                      </ActionBar>
                    </div>
                  </Card.Section>
                </Card>
              ) : null}

            </>
          ) : null}

          {selected === 2 ? (
            <>
              <Card
                title={t("Logistics")}
                subtitle={t("Configure logistics settings for this variant.")}
                titleActions={[
                  {
                    action: () => updateFilterStateBoolean("showAccountingReference"),
                    content: t("Edit Accounting Reference"),
                  },
                ]}
              >
                {sectionDivider}

                <Card.Section>
                  <FormLayout>
                    <FormLayout.Group>
                      <Input
                        value={variantState.barcode}
                        label={t("Barcode")}
                        id="barcode"
                        onChange={handleInputChange}
                        disabled={!productPermissions?.products_edit || !facilityPermissions?.products_edit}
                      />
                      <Input
                        value={variantState.sku}
                        label={t("Sku")}
                        id="sku"
                        onChange={handleInputChange}
                        disabled={!productPermissions?.products_edit || !facilityPermissions?.products_edit}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <Toggle
                        id={"cannot_be_sold"}
                        labelTop={t("secure.facility.product.variant.052")}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToggleChange(e)}
                        checked={variantState?.meta?.cannot_be_sold === 0 ? false : true}
                        disabled={variantState?.cannot_be_sold}
                        labelSupport={variantState.cannot_be_sold ? t("secure.facility.product.variant.053") : ""}
                      ></Toggle>
                    </FormLayout.Group>
                    {filterState.showAccountingReference && (
                      <FormLayout.Group>
                        <Select
                          label={t("Accounting Reference")}
                          defaultValue={defaultAccountingReference.id}
                          showSearch
                          className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                          onSearch={(query: string) => handleSelectOnSearch(query)}
                          onChange={(value: any) =>
                            setVariantState({ ...variantState, accounting_reference_id: value })
                          }
                          allowClear
                          searchValue={filterState.search}
                          showDropDownOnFocus={true}
                          disabled={!productPermissions?.products_edit || !facilityPermissions?.products_edit}
                        >
                          {accountingReferences
                            .filter(accountingReference =>
                              accountingReferenceMatchesQuery(
                                accountingReference.title,
                                accountingReference.reference_number,
                                filterState.search,
                              ),
                            )
                            .map((reference: TAccountingReference, index: number) => {
                              const label = formatAccountingReferenceLabel(
                                reference.title,
                                reference.reference_number,
                              );
                              return (
                                <div key={index}>
                                  {filterState.search !== "" ? (
                                    <Option key={index} value={reference.id} name={label}>
                                      <div>
                                        <div className="font-semibold text-lg">{label}</div>
                                      </div>
                                    </Option>
                                  ) : null}
                                </div>
                              );
                            })}
                        </Select>
                      </FormLayout.Group>
                    )}
                  </FormLayout>
                </Card.Section>
              </Card>


              {variantState.showTickets && (
                <Card
                  title={t("secure.facility.product.variant.011")}
                  titleActions={[{ content: t("secure.facility.product.variant.012"), action: openAddTicket }]}
                >
                  <Card.Section>
                    <div className="ui-checkbox-group">
                      {variantState.tickets?.map((ticket: any, index: number) => {
                        return (
                          <div key={index} className="ui-checkbox-group-item">
                            <div className="ui-checkbox-group-item-content">
                              <div className="ui-checkbox-group-item-text">
                                <p className="text-sm text-medium text-gray-700">{ticket.title}</p>
                                <p className="text-xs text-regular text-gray-500">{ticket.subtitle}</p>
                                <p className="text-xs text-regular text-gray-500">{ticket.included}</p>
                                <Button type="link" size="small" onClick={(e: any) => handleDeleteTicket(e, ticket)}>
                                  {t("secure.facility.product.variant.013")}
                                </Button>
                              </div>
                            </div>
                            <Input
                              onChange={(e: any) => handleTicketQuantityChange(e, ticket)}
                              value={ticket.quantity}
                              type="number"
                              label=""
                            />
                          </div>
                        );
                      })}
                    </div>
                  </Card.Section>
                </Card>
              )}

              {variantState.product?.track_inventory == true &&
                <Card
                  title={
                    <span className="flex flex-row items-center gap-2">
                      {t("Inventory History")}
                      {variantState.product?.track_inventory == true && <Badge size="small" type="primary">
                        {variantState.inventory_item?.inventory_level?.quantity_available ?? 0}
                        &nbsp; {t(" in stock")}
                      </Badge>}
                    </span>
                  }
                  subtitle={t("Receiving, storing, and tracking products in a warehouse or facility to ensure availability.")}
                  titleActions={
                    productPermissions?.products_edit && variantState.product?.warehouse_product_id === null
                      ? [{
                        content: <>
                          {t("Adjust Quantity")}
                          <FontAwesomeIcon icon={faSlidersSimple} className="ml-2" />
                        </>,
                        action: () => setAdjustQuantity(true),
                      }]
                      : null
                  }
                >
                  {sectionDivider}
                  <Card.Section>
                    <>
                      <InventoryHistoryTable
                        variant={variant}
                        unit={productUnit?.abbreviation}
                        adjustQuantity={adjustQuantity}
                        onSaveAdjustment={completeUpdateInventory}
                        onCancelAdjustment={() => setAdjustQuantity(false)}
                      />
                      <div className="flex justify-center">
                        <Button type="tertiary" onClick={displayInventoryLogs}>
                          {t('View more')}
                        </Button>
                      </div>
                      <div className="border-gray-200 border-b my-4"></div>
                      <p className="text-lg text-semibold mb-3">{t("Register Transfers")}</p>
                      <RegisterInventoryTransferTable
                        variantId={variant?.id}
                        unit={productUnit?.abbreviation}
                      />
                    </>
                  </Card.Section>
                </Card>
              }

              {!variantState.product?.warehouse ? (
                <Card
                  title={t("Warehouse Inventory")}
                  subtitle={t("Modify and manage warehouse products and its inventory.")}
                  collapsable={true}
                  defaultCollapsed={false}
                >
                  {sectionDivider}
                  {/* WAREHOUSE */}
                  <Card.Section>
                    <DataTable
                      columns={[{ label: t("Warehouse Product") }, { label: t("Inventory Units") }]}
                    >
                      {variantState?.warehouse_products?.map((product, index) => {
                        return (
                          <tr key={index}>
                            <td>{product?.preferred_title ? product?.preferred_title : product?.title}</td>
                            <td>
                              <Input
                                labelHidden
                                placeholder={t("Inventory units")}
                                value={product?.inventory_units ?? ""}
                                onChange={e => onInventoryUnitsChange(e, index)}
                                type="number"
                                suffix={
                                  productUnits?.find(unit => unit.id === variantState.product?.product_unit_id)?.abbreviation
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </DataTable>
                  </Card.Section>
                </Card>
              ) : null}
              {/* INVENTORY BY FACILITY */}
              {variantState.product?.track_inventory ? <Card
                title={t("Inventory by Facility")}
                collapsable={true}
                defaultCollapsed={false}
                subtitle={t("Manage inventory and availability for this product across facilities.")}
                titleActions={[
                  {
                    content: showAllFacilityInventories
                      ? t("Hide Secondary Locations")
                      : t("View All Locations"),
                    action: () => setShowAllFacilityInventories(!showAllFacilityInventories),
                    disabled: false,
                  },
                ]}
              >
                {sectionDivider}
                <Card.Section>
                  <div className="product-edit-inventory-table">
                    <FacilityInventoryTable
                      mainFacilityId={facilityStore.facility?.id || 0}
                      facilities={facilityStore.facilities}
                      variants={[variantState]}
                      showAllFacilities={showAllFacilityInventories}
                    />
                  </div>
                </Card.Section>
              </Card> : null}
              {variantState.product?.type === "Membership" || variantState.product?.type === "Membership Add On" ? (<Card
                title={t("Memberships Attached to This Product")}
                subtitle={t("This product is attached to the following memberships")}
                titleActions={[
                ]}
              //defaultCollapsed
              //collapsable={false}
              >
                {sectionDivider}
                <Card.Section>
                  {membershipState.loading ? (
                    <div className="flex justify-center py-8">
                      <Spin />
                    </div>
                  ) : (
                    <DataTable
                      columns={[
                        { label: t("Membership Title"), width: "35%" },
                        { label: t("Subtitle"), width: "25%" },
                        { label: t("Type"), width: "15%" },
                        { label: t("Variant"), width: "15%" },
                        { label: t("Actions"), width: "10%" },
                      ]}
                      loading={membershipState.loading}
                    >
                      {attachedMemberships.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center py-4">
                            {t("No memberships attached to this product")}
                          </td>
                        </tr>
                      ) : (
                        attachedMemberships.map(membership => (
                          <tr key={membership.id}>
                            <td>
                              <div>
                                <p className="lead-text">{membership.title}</p>
                              </div>
                            </td>
                            <td>{membership.subtitle || "-"}</td>
                            <td>
                              <Badge type="primary">
                                {membership.membership_type === "membership"
                                  ? t("Membership")
                                  : t("Add-on")}
                              </Badge>
                            </td>
                            <td>
                              {membership.variant_id && variantState.product?.variants?.length > 1
                                ? variantState?.title
                                : "-"}
                            </td>
                            <td>
                              <div className="flex gap-2 items-center justify-end">
                                <ButtonNew
                                  type="text"
                                  size="xsmall"
                                  onClick={() => history.push(`/admin/membership/${membership.id}`)}
                                >
                                  {t("View")}
                                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-2" />
                                </ButtonNew>
                                <ButtonNew
                                  type="text"
                                  size="xsmall"
                                  onClick={() => updateAttachMembershipModal({ isOpen: true })}
                                >
                                  <FontAwesomeIcon icon={faArrowLeftArrowRight} />
                                </ButtonNew>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </DataTable>
                  )}
                </Card.Section>
              </Card>) : null}
            </>
          ) : null}
        </Page.Section>
      </Page >

      <ERangeBucketSizeModal
        open={eRangeBucketModalOpen}
        productTitle={variantState.product?.title}
        onCancel={() => {
          setSaveAfterBucketSelect(false);
          setERangeBucketModalOpen(false);
        }}
        onConfirm={size => {
          setERangeBucketSize(size);
          setERangeBucketModalOpen(false);
          if (saveAfterBucketSelect) {
            setSaveAfterBucketSelect(false);
            void saveVariant(size);
          }
        }}
      />

      <Popup
        okText={t("secure.facility.product.variant.054")}
        onCancel={() => setDuplicateVariantOpen(false)}
        onOk={duplicateVariant}
        title={t("secure.facility.product.variant.055")}
        type="warning"
        description={t("secure.facility.product.variant.056")}
        open={duplicateVariantOpen}
      />

      {/* Add Ticket Sheet - Handles adding ticket to product */}
      <Sheet
        open={addTicketState.addTicketActive}
        title={t("secure.facility.product.variant.022")}
        size="small"
        closable
        onCancel={closeAddTicket}
        cancelText={t("secure.facility.product.variant.023")}
        backDropCancel={false}
      >
        <div className="ui-checkbox-group">
          <div className="mb-4 w-full">
            <Input
              value={addTicketState.ticketSearch}
              onChange={(value: any) =>
                setAddTicketState(prevState => ({ ...prevState, ticketSearch: value.target.value }))
              }
              type="search"
              placeholder={t("secure.facility.product.variant.029")}
            ></Input>
          </div>

          {addTicketState.allTickets?.map((ticket: any, index: number) => {
            return (
              <div key={index} onClick={(e: any) => handleAddTicket(e, ticket)} className="ui-checkbox-group-item">
                <div className="ui-checkbox-group-item-content">
                  <div className="ui-checkbox-group-item-text">
                    <p className="text-sm text-medium text-gray-700">{ticket.title}</p>
                    <p className="text-xs text-regular text-gray-500">{ticket.subtitle}</p>
                    <p className="text-sm text-regular text-gray-600">{ticket.included}</p>
                  </div>
                </div>
                <Checkbox
                  size="medium"
                  value={ticket.id}
                  checked={false}
                  onChange={(e: any) => handleAddTicket(e, ticket)}
                />
              </div>
            );
          })}
        </div>
      </Sheet>

      <Sheet
        open={showInventoryLogs}
        size="small"
        closable
        title={t("secure.facility.product.variant.024")}
        cancelText={t("Close")}
        onCancel={() => setShowInventoryLogs(false)}
      >
        <div className="flex flex-column">
          {inventoryLogs.map((logs, index) => (
            <InventoryLogs
              title={logs.type}
              action={logs.type}
              description={logs.description}
              key={index}
              previous_quantity={logs.previous_quantity}
              new_quantity={logs.new_quantity}
              last={index + 1 === inventoryLogs.length ? true : false}
              created_at={logs.created_at}
            />
          ))}
        </div>
      </Sheet>

      <Portal isMounted={comboProductState.comboProductsActive}>
        <AttachProductAsComboModal
          isVisible={comboProductState.comboProductsActive}
          currentlyAddedProducts={comboProductState.currentAddedProducts}
          userLevel={"facility"}
          onCancel={() => setComboProductState(prev => ({ ...prev, comboProductsActive: false }))}
          onOk={productId => addComboProduct({ base_variant_id: Number(variantId), variant_id: productId })}
        />
      </Portal>

      <Portal isMounted={comboProductState.updateComboProductsOpen}>
        <UpdateProductAsComboModal
          isVisible={comboProductState.updateComboProductsOpen}
          onCancel={() =>
            setComboProductState(prevState => ({
              ...prevState,
              updateComboProductsOpen: false,
              selectedComboVariant: null,
            }))
          }
          onOk={(comboId, cost, price, quantity) => handleUpdateComboProductModalOk(comboId, cost, price, quantity)}
          combo={comboProductState.selectedComboVariant}
          handleUpdates={newState =>
            setComboProductState(prevState => ({ ...prevState, selectedComboVariant: newState.selectedComboVariant }))
          }
        />
      </Portal>

      <Portal isMounted={attachMembershipModal.isOpen}>
        <Sheet
          open={attachMembershipModal.isOpen}
          title={t("Attach Membership")}
          size="medium"
          closable
          onCancel={() => {
            closeAttachMembershipModal();
            setSelectedMembershipId(null);
            setMembershipSearchQuery("");
          }}
          onOk={handleAttachMembership}
          okText={t("Attach")}
          cancelText={t("Cancel")}
          okDisabled={!selectedMembershipId}
          backDropCancel={false}
        >
          <div className="mb-4">
            <Search
              searchCallback={value => setMembershipSearchQuery(value)}
              historyKey="attach-membership-search"
              placeholder={t("Search memberships...")}
            />
          </div>

          <div style={{ minHeight: "300px" }}>
            {availableMemberships === undefined ? (
              <div className="flex justify-center items-center" style={{ height: "300px" }}>
                <Spin />
              </div>
            ) : (
              <DataTable
                columns={[
                  { label: "", width: "5%" },
                  { label: t("Membership"), width: "50%" },
                  { label: t("Type"), width: "20%" },
                  { label: t("Status"), width: "15%" },
                  { label: t("Customer Type"), width: "10%" },
                ]}
              >
                {availableMemberships?.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      {t("No memberships available to attach")}
                    </td>
                  </tr>
                ) : (
                  availableMemberships?.map(membership => (
                    <tr
                      key={membership.id}
                      onClick={() => setSelectedMembershipId(membership.id)}
                      className="clickable"
                      style={{
                        backgroundColor: selectedMembershipId === membership.id ? "#F9FAFB" : "transparent",
                      }}
                    >
                      <td>
                        <input
                          type="radio"
                          checked={selectedMembershipId === membership.id}
                          onChange={() => setSelectedMembershipId(membership.id)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td>
                        <div>
                          <p className="lead-text">{membership.title}</p>
                          {membership.subtitle && <p className="support-text">{membership.subtitle}</p>}
                        </div>
                      </td>
                      <td>
                        <Badge type="primary">
                          {membership.membership_type === "membership" ? t("Membership") : t("Add-on")}
                        </Badge>
                      </td>
                      <td>
                        <Badge type={membership.archived ? "error" : "success"}>
                          {membership.archived ? t("Archived") : t("Active")}
                        </Badge>
                      </td>
                      <td>{membership.customer_type_title || "-"}</td>
                    </tr>
                  ))
                )}
              </DataTable>
            )}
          </div>
        </Sheet>
      </Portal>

    </>
  );
}
